import type { PropsWithChildren } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import { GovFlex, GovIcon, GovLink } from '@gov-design-system-ce/react';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type {
	SearchAllRecentVisited,
	SearchAllSearchTerms,
	SearchAllTypesResult,
	SearchResultSection,
} from '@gov-nx/module/page';
import {
	distributeSearchResultsIntoSections,
	resolveLinkFromActionParam,
	SearchResultSectionType,
	useHledaniContext,
} from '@gov-nx/module/page';
import { Button } from '../../Button/Button';
import { SearchHighlight } from '../../Search/SearchHighlight';
import { SearchItemActionResolver } from '../../Search/SearchItemActionResolver';

const order = [
	SearchResultSectionType.recentSearches,
	SearchResultSectionType.recentVisited,
	SearchResultSectionType.searchResults,
	SearchResultSectionType.popular,
];

const MAX_TOTAL = 10;

export const SearchResultsAutocomplete = () => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Global);
	const {
		results,
		searchTerms,
		searchValue,
		isSearchValue,
		removeSearchTerm,
		onSearch,
		getSearchResultsPageUrl,
		numberOfResults,
		recommendedResults,
		currentSearchValue,
		onSubmit,
		recentVisited,
		resolveAction,
		isSearchOpened,
		formDefinition,
	} = useHledaniContext();

	// Filter searchTerms and recentVisited when searchValue is present
	const filteredSearchTerms = searchValue
		? searchTerms.filter((term) =>
				term.searchTerm.toLowerCase().includes(searchValue.toLowerCase())
			)
		: searchTerms;

	const filteredRecentVisited = searchValue
		? recentVisited.filter((item) =>
				item.title.toLowerCase().includes(searchValue.toLowerCase())
			)
		: recentVisited;

	const sections: SearchResultSection<
		SearchAllTypesResult | SearchAllRecentVisited | SearchAllSearchTerms
	>[] = [
		{
			key: SearchResultSectionType.recentSearches,
			title: t('vyhledat.sekce.naposledy-hledane'),
			items: [...filteredSearchTerms].reverse(),
		},
		{
			key: SearchResultSectionType.searchResults,
			title: t('vyhledat.sekce.vysledky-hledani'),
			items: results,
		},
		{
			key: SearchResultSectionType.recentVisited,
			title: t('vyhledat.sekce.naposledy-navstivene'),
			items: [...filteredRecentVisited].reverse(),
		},
		{
			key: SearchResultSectionType.popular,
			title: t('vyhledat.sekce.doporuceno'),
			items: [...recommendedResults],
		},
	];

	const limitedSections = distributeSearchResultsIntoSections(
		sections,
		isSearchValue,
		MAX_TOTAL
	);

	const orderedSections = order
		.map((key) => limitedSections.find((s) => s.key === key))
		.filter(Boolean) as SearchResultSection<
		SearchAllTypesResult | SearchAllRecentVisited | SearchAllSearchTerms
	>[];

	if (!orderedSections.some((sec) => sec.items.length > 0)) {
		return null;
	}

	return (
		<section className="gov-suggestions">
			<ul className="gov-suggestions__results gov-list--plain">
				{orderedSections
					.filter((sec) => sec.items.length > 0)
					.map((sec) => {
						if (sec.key === SearchResultSectionType.recentSearches) {
							const items = sec.items as SearchAllSearchTerms[];
							return (
								<SearchResultsSection
									key={sec.key}
									id={sec.key}
									title={sec.title}>
									{items.map((item) => (
										<li key={item.searchTerm}>
											<GovFlex
												gap="s"
												justifyContent="space-between"
												alignItems="center"
												responsive="false">
												<GovLink
													onClick={() => {
														formDefinition.formMethods.setValue(
															'search',
															item.searchTerm
														);
														onSearch();
													}}
													asChild={true}
													size="m"
													truncate
													iconStart={
														<GovIcon
															slot="icon-start"
															type="templates"
															name="clock-history"
															size="m"
															aria-hidden="true"
														/>
													}>
													<Link to={getSearchResultsPageUrl(item.searchTerm)}>
														<SearchHighlight
															text={item.searchTerm}
															keyword={currentSearchValue ?? ''}
														/>
													</Link>
												</GovLink>
												<Button
													color="neutral"
													size="xs"
													type="base"
													aria-label={
														t('akce.wcag.odstranit-vyraz') +
														' ' +
														item.searchTerm
													}
													onClick={() => removeSearchTerm(item.searchTerm)}
													iconStart={
														<GovIcon
															type="components"
															name="x-lg"
														/>
													}
												/>
											</GovFlex>
										</li>
									))}
								</SearchResultsSection>
							);
						}

						if (sec.key === SearchResultSectionType.recentVisited) {
							const items = sec.items as SearchAllRecentVisited[];
							return (
								<SearchResultsSection
									key={sec.key}
									id={sec.key}
									title={sec.title}>
									{items.map((item, idx) => {
										const link = resolveLinkFromActionParam(item.actionParams);
										link.onAfterClick = () => {
											isSearchOpened.setFalse();
										};
										const action = () => {
											isSearchOpened.setFalse();
											resolveAction(item);
										};
										return (
											<SearchResultItem
												// eslint-disable-next-line react/no-array-index-key
												key={idx}
												item={{
													...item,
													link,
													action,
													titleForSorting: item.title,
												}}
												elementClassname="flex w-full max-w-full min-h-[40px] items-center"
												spanClassname="flex-1 min-w-0 truncate text-(--text-primary-color)"
											/>
										);
									})}
								</SearchResultsSection>
							);
						}

						if (sec.key === SearchResultSectionType.searchResults) {
							const items = sec.items as SearchAllTypesResult[];
							return (
								<SearchResultsSection
									key={sec.key}
									id={sec.key}
									title={sec.title}>
									{items.map((item, idx) => {
										if (item.link) {
											item.link.onAfterClick = () => {
												isSearchOpened.setFalse();
											};
										}

										return (
											<SearchResultItem
												// eslint-disable-next-line react/no-array-index-key
												key={idx}
												item={item}
											/>
										);
									})}
									{numberOfResults > items.length && (
										<li>
											<Button
												onClick={onSubmit}
												color="primary"
												size="s"
												type="solid"
												aria-label={t('akce.wcag.zobrazit-vsechny-vysledky', {
													count: numberOfResults,
												})}
												className=" gov-suggestions__all-results"
												isExpanded>
												{t('akce.zobrazit-vsechny-vysledky')}({numberOfResults})
											</Button>
										</li>
									)}
								</SearchResultsSection>
							);
						}

						if (sec.key === SearchResultSectionType.popular) {
							const items = sec.items as SearchAllTypesResult[];
							return (
								<SearchResultsSection
									key={sec.key}
									id={sec.key}
									title={sec.title}>
									{items.map((item, idx) => (
										<SearchResultItem
											// eslint-disable-next-line react/no-array-index-key
											key={idx}
											item={item}
										/>
									))}
								</SearchResultsSection>
							);
						}

						return null;
					})}
			</ul>
		</section>
	);
};

const SearchResultItem = ({
	item,
	elementClassname = 'flex w-full max-w-full min-h-[40px] items-center',
	spanClassname = 'flex-1 min-w-0 truncate text-(--text-primary-color) rich-html',
}: {
	item: Pick<
		SearchAllTypesResult,
		'title' | 'titleForSorting' | 'action' | 'link'
	>;
	elementClassname?: string;
	spanClassname?: string;
}) => (
	<li>
		<SearchItemActionResolver
			action={item.action}
			link={item.link}
			className={elementClassname}
			ariaLabel={item.titleForSorting}>
			<span
				className={spanClassname}
				dangerouslySetInnerHTML={{ __html: item.title }}
			/>
		</SearchItemActionResolver>
	</li>
);

interface SearchResultsSectionProps extends PropsWithChildren {
	id: string;
	title: string;
}

const SearchResultsSection = ({
	id,
	title,
	children,
}: SearchResultsSectionProps) => (
	<li
		key={id}
		aria-labelledby={id}>
		<strong
			id={id}
			className="text-(--text-secondary)">
			{title}
		</strong>
		<ul className="gov-list--plain">{children}</ul>
	</li>
);
